/**
 * TWO WAYS TO LIGHT THE SAME HOUSE, SIDE BY SIDE.
 *
 * The workflow document is specific about the shape of this: compare two or
 * three MEANINGFUL packages with the differences highlighted, not a wall of
 * nine similar cards. Nine cards is what a form generates when nobody decided
 * what the choice actually is; two is what a contractor puts in front of a
 * homeowner on a phone in a driveway.
 *
 * A package here is one real decision - which product goes on the roof - and
 * everything else follows from it. Product spacing is a physical fact, so
 * changing the product changes the bulb count, the clip count and the look,
 * and nothing else about the job moves: the same sections, the same measured
 * lengths, the same house.
 *
 * WHAT IS DELIBERATELY NOT HERE IS MONEY. ACCEPTANCE.md says the customer
 * comparison excludes direct cost and margin, and beyond that, what a package
 * costs depends on a rate per product family that nobody has decided yet. A
 * comparison that invented one would be the most convincing wrong number in
 * the product. The pricing engine and the seasonal ladder are untouched, and
 * the missing decision is flagged rather than guessed.
 */

import type { ProductId } from '../types/document'
import { PRODUCTS } from './products'
import type { RoofSection } from './sections'
import { takeoff, type Takeoff } from './materials'

export interface Package {
  id: string
  /** What a homeowner would call it. */
  name: string
  product: ProductId
  /** One line on why somebody picks this one. */
  why: string
}

/**
 * The two that are actually different from each other on a roofline. C7 is a
 * slightly smaller C9 at the same twelve inch spacing, so offering it as a
 * third choice would be a third card with the same numbers on it - which is
 * exactly the wall the workflow document warns about.
 */
export const PACKAGES: Package[] = [
  {
    id: 'classic',
    name: 'Classic C9',
    product: 'C9',
    why: 'Big warm bulbs a foot apart. Reads from the street.',
  },
  {
    id: 'permanent',
    name: 'Permanent track',
    product: 'permanent',
    why: 'Low profile, tight spacing, stays up all year.',
  },
]

export interface PackageOption {
  pkg: Package
  takeoff: Takeoff
  spacingInches: number
  /** How this differs from the package it is being compared against. */
  differences: string[]
}

/**
 * COMPARE, WITH THE DIFFERENCES SAID OUT LOUD.
 *
 * The first package is the reference and carries no differences of its own.
 * Everything after it says what changes, in the customer's terms, and only
 * where something actually changed - a difference list padded with lines
 * saying "same footage" is how a comparison stops being read.
 */
export function comparePackages(sections: RoofSection[], list: Package[] = PACKAGES): PackageOption[] {
  const options = list.map((pkg) => {
    const spec = PRODUCTS[pkg.product]
    return {
      pkg,
      spacingInches: spec.spacingInches,
      takeoff: takeoff(sections, spec.spacingInches),
      differences: [] as string[],
    }
  })

  if (options.length < 2) return options
  const base = options[0]
  const bulbsOf = (o: PackageOption) => o.takeoff.lines.find((l) => l.key === 'bulbs')?.qty ?? 0

  for (const o of options.slice(1)) {
    const d: string[] = []
    const mine = bulbsOf(o)
    const theirs = bulbsOf(base)

    if (o.spacingInches !== base.spacingInches) {
      d.push(`${o.spacingInches} inches apart instead of ${base.spacingInches}.`)
    }
    if (mine !== theirs) {
      const more = mine - theirs
      d.push(
        more > 0
          ? `${more} more bulbs and clips than ${base.pkg.name}.`
          : `${-more} fewer bulbs and clips than ${base.pkg.name}.`,
      )
    }
    // Footage is the house, so it never differs. Saying so would be padding.
    o.differences = d
  }

  return options
}

/**
 * THE DECISION NOBODY HAS MADE, NAMED WHERE A SCREEN CAN SHOW IT.
 *
 * Not a placeholder price and not a hidden assumption. A package cannot carry
 * a customer price until somebody decides what a foot of each product family
 * sells for, and that is an owner's decision about their own business, not a
 * default this program gets to pick.
 */
export const PRICE_UNDECIDED =
  'What each package costs is not set. A price per foot for each product family has to be decided before this can show one.'
